import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Upload, Link2, Trash2, Edit, Image, Video, Eye, 
  MoreVertical, Search, Filter, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreativesPage() {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sourceType, setSourceType] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    creative_type: 'image',
    source_type: 'url',
    file_url: '',
    duration_seconds: 10,
    width: 1920,
    height: 1080,
    click_url: '',
    tags: ''
  });

  const fetchCreatives = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creatives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreatives(data);
      }
    } catch (error) {
      toast.error('Failed to fetch creatives');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreatives();
  }, [fetchCreatives]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      const res = await fetch(`${API_URL}/api/creatives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Creative created successfully');
        setShowDialog(false);
        resetForm();
        fetchCreatives();
      } else {
        const text = await res.text();
        let errorMsg = 'Failed to create creative';
        try {
          const err = JSON.parse(text);
          errorMsg = err.detail || errorMsg;
        } catch {
          if (text) errorMsg = text;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Failed to create creative');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    // Auto-fill name if empty
    if (!formData.name) {
      setFormData({ ...formData, name: file.name.split('.')[0] });
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }
    
    if (!formData.name) {
      toast.error('Please enter a name for the creative');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);
      formDataUpload.append('name', formData.name);
      formDataUpload.append('duration_seconds', formData.duration_seconds.toString());
      if (formData.click_url) {
        formDataUpload.append('click_url', formData.click_url);
      }
      
      const res = await fetch(`${API_URL}/api/creatives/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (res.ok) {
        toast.success('Creative uploaded successfully');
        setShowDialog(false);
        resetForm();
        setSelectedFile(null);
        fetchCreatives();
      } else {
        const text = await res.text();
        let errorMsg = 'Failed to upload creative';
        try {
          const err = JSON.parse(text);
          errorMsg = err.detail || errorMsg;
        } catch {
          if (text) errorMsg = text;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Failed to upload creative');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this creative?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creatives/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Creative deleted');
        fetchCreatives();
      } else {
        toast.error('Failed to delete creative');
      }
    } catch (error) {
      toast.error('Failed to delete creative');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      creative_type: 'image',
      source_type: 'url',
      file_url: '',
      duration_seconds: 10,
      width: 1920,
      height: 1080,
      click_url: '',
      tags: ''
    });
    setSourceType('url');
    setSelectedFile(null);
  };

  const filteredCreatives = creatives.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || c.creative_type === filterType;
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Creatives</h1>
          <p className="text-zinc-400 mt-1">Manage your ad creatives (images and videos)</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Creative
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Creatives</p>
                <p className="text-2xl font-bold text-white">{creatives.length}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Image className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Images</p>
                <p className="text-2xl font-bold text-white">
                  {creatives.filter(c => c.creative_type === 'image').length}
                </p>
              </div>
              <div className="p-3 bg-emerald-500/20 rounded-lg">
                <Image className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Videos</p>
                <p className="text-2xl font-bold text-white">
                  {creatives.filter(c => c.creative_type === 'video').length}
                </p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Video className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Impressions</p>
                <p className="text-2xl font-bold text-white">
                  {formatNumber(creatives.reduce((sum, c) => sum + (c.impressions || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-amber-500/20 rounded-lg">
                <Eye className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search creatives..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-700"
                />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-700">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-700">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Creatives Table */}
      <Card className="glass-card border-zinc-800">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500">Preview</TableHead>
              <TableHead className="text-zinc-500">Name</TableHead>
              <TableHead className="text-zinc-500">Type</TableHead>
              <TableHead className="text-zinc-500">Dimensions</TableHead>
              <TableHead className="text-zinc-500">Duration</TableHead>
              <TableHead className="text-zinc-500">Impressions</TableHead>
              <TableHead className="text-zinc-500">Clicks</TableHead>
              <TableHead className="text-zinc-500">CTR</TableHead>
              <TableHead className="text-zinc-500">Status</TableHead>
              <TableHead className="text-zinc-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-zinc-500">
                  Loading creatives...
                </TableCell>
              </TableRow>
            ) : filteredCreatives.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-zinc-500">
                  No creatives found. Create your first creative!
                </TableCell>
              </TableRow>
            ) : (
              filteredCreatives.map((creative) => (
                <TableRow key={creative.id} className="border-zinc-800" data-testid={`creative-row-${creative.id}`}>
                  <TableCell>
                    <div 
                      className="w-16 h-10 bg-zinc-800 rounded overflow-hidden cursor-pointer hover:ring-2 ring-blue-500"
                      onClick={() => { setSelectedCreative(creative); setShowPreview(true); }}
                    >
                      {creative.creative_type === 'image' ? (
                        <img 
                          src={creative.file_url.startsWith('http') ? creative.file_url : `${API_URL}${creative.file_url}`} 
                          alt={creative.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-5 h-5 text-zinc-500" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-white">{creative.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={creative.creative_type === 'video' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                      {creative.creative_type === 'video' ? <Video className="w-3 h-3 mr-1" /> : <Image className="w-3 h-3 mr-1" />}
                      {creative.creative_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-400">{creative.width}x{creative.height}</TableCell>
                  <TableCell className="text-zinc-400">{creative.duration_seconds}s</TableCell>
                  <TableCell className="text-zinc-300">{formatNumber(creative.impressions)}</TableCell>
                  <TableCell className="text-zinc-300">{formatNumber(creative.clicks)}</TableCell>
                  <TableCell className="text-zinc-300">
                    {creative.impressions > 0 ? ((creative.clicks / creative.impressions) * 100).toFixed(2) : '0.00'}%
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      creative.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                      creative.status === 'paused' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }>
                      {creative.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                        <DropdownMenuItem onClick={() => { setSelectedCreative(creative); setShowPreview(true); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </DropdownMenuItem>
                        {creative.click_url && (
                          <DropdownMenuItem onClick={() => window.open(creative.click_url, '_blank')}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Click URL
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDelete(creative.id)} className="text-rose-400 focus:text-rose-400">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add Creative Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Add Creative</DialogTitle>
          </DialogHeader>
          
          <Tabs value={sourceType} onValueChange={setSourceType}>
            <TabsList className="grid w-full grid-cols-2 bg-zinc-800">
              <TabsTrigger value="url" className="data-[state=active]:bg-blue-600">
                <Link2 className="w-4 h-4 mr-2" />
                URL
              </TabsTrigger>
              <TabsTrigger value="upload" className="data-[state=active]:bg-blue-600">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="url" className="space-y-4 mt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Creative"
                    className="bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Type</Label>
                  <Select value={formData.creative_type} onValueChange={(v) => setFormData({ ...formData, creative_type: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">File URL</Label>
                  <Input
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://example.com/creative.jpg"
                    className="bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Duration (s)</Label>
                    <Input
                      type="number"
                      value={formData.duration_seconds}
                      onChange={(e) => setFormData({ ...formData, duration_seconds: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Width</Label>
                    <Input
                      type="number"
                      value={formData.width}
                      onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Height</Label>
                    <Input
                      type="number"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Click URL (optional)</Label>
                  <Input
                    value={formData.click_url}
                    onChange={(e) => setFormData({ ...formData, click_url: e.target.value })}
                    placeholder="https://example.com/landing"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Tags (comma-separated)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="summer, promo, outdoor"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Creative</Button>
                </DialogFooter>
              </form>
            </TabsContent>
            
            <TabsContent value="upload" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Creative"
                    className="bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Duration (seconds)</Label>
                  <Input
                    type="number"
                    value={formData.duration_seconds}
                    onChange={(e) => setFormData({ ...formData, duration_seconds: parseInt(e.target.value) || 10 })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Click URL (optional)</Label>
                  <Input
                    value={formData.click_url}
                    onChange={(e) => setFormData({ ...formData, click_url: e.target.value })}
                    placeholder="https://example.com/landing"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Select File *</Label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${selectedFile ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-blue-500'}`}>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="creative-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="creative-upload" className="cursor-pointer">
                      {selectedFile ? (
                        <>
                          <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Image className="w-4 h-4 text-emerald-400" />
                          </div>
                          <p className="text-emerald-400 font-medium">{selectedFile.name}</p>
                          <p className="text-zinc-500 text-sm mt-1">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB - Click to change
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                          <p className="text-zinc-400">Click to select image or video</p>
                          <p className="text-zinc-500 text-sm mt-1">JPG, PNG, GIF, MP4, WebM</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
                  <Button 
                    type="button" 
                    onClick={handleFileUpload} 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={uploading || !selectedFile || !formData.name}
                  >
                    {uploading ? 'Uploading...' : 'Upload Creative'}
                  </Button>
                </DialogFooter>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedCreative?.name}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {selectedCreative?.creative_type === 'image' ? (
              <img 
                src={selectedCreative?.file_url.startsWith('http') ? selectedCreative?.file_url : `${API_URL}${selectedCreative?.file_url}`}
                alt={selectedCreative?.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <video 
                src={selectedCreative?.file_url.startsWith('http') ? selectedCreative?.file_url : `${API_URL}${selectedCreative?.file_url}`}
                controls
                className="w-full h-full"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-500">Dimensions:</span>
              <span className="text-white ml-2">{selectedCreative?.width}x{selectedCreative?.height}</span>
            </div>
            <div>
              <span className="text-zinc-500">Duration:</span>
              <span className="text-white ml-2">{selectedCreative?.duration_seconds}s</span>
            </div>
            <div>
              <span className="text-zinc-500">Impressions:</span>
              <span className="text-white ml-2">{formatNumber(selectedCreative?.impressions)}</span>
            </div>
            <div>
              <span className="text-zinc-500">Clicks:</span>
              <span className="text-white ml-2">{formatNumber(selectedCreative?.clicks)}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
